/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import Loading from 'components/Loading/Loading';
import Form from 'forms/_.extend';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';
import ReassignAssetForm from './ReassignAssetForm/ReassignAssetForm';
import ReassignAssetSuccess from './ReassignAssetSuccess/ReassignAssetSuccess';
import ReassignAssetError from './ReassignAssetError/ReassignAssetError';
import styles from '../../../ActionDrawer.module.scss';
import moment from 'moment';
import type { TranslatorProps } from 'react-i18next';
import type TypeActionDrawerStore from 'stores/ActionDrawerStore';
type TypeProps = {
  autoClose: boolean,
  t: TranslatorProps,
  actionDrawerStore: TypeActionDrawerStore,
  assetStore: Object,
};

@translate()
@inject('actionDrawerStore', 'assetStore')
@observer
export default class ReassignAsset extends React.Component<*> {
  createForm = () => {
    const { t, actionDrawerStore, assetStore }: TypeProps = this.props;
    const assets = this.props.formData;

    const fields = [
      {
        name: 'reassignAssetTo',
        label: t('general:actions.reassign_asset_to'),
        fields: [
          {
            name: 'assigneeId',
            label: t('general:labels.assignee'),
            rules: 'required|string',
            value: '',
            placeholder: t('general:placeholders.type_user'),
          },
        ],
      },
      {
        name: 'assetWarnings',
        fields: assets.map(asset => {
          const calDueDate = moment(
            asset.calibration ? asset.calibration.due_date : asset.er_calibration_due_date
          ).diff(moment(), 'days');
          const isOutOfCal = asset.calibration
            ? asset.calibration.status === 'Out of Cal'
            : asset.er_calibration_due_status === 'Out of Cal';

          return {
            name: `${asset.id}`,
            value: {
              out_of_cal: isOutOfCal,
              due_to_go_out_of_cal: calDueDate <= 30 && !isOutOfCal,
            },
            extra: {
              warnings: [
                {
                  name: 'out_of_cal',
                  message: t('general:assets.messages.calibration_expired'),
                  link: null,
                },
                {
                  name: 'due_to_go_out_of_cal',
                  message: t('general:assets.messages.calibration_expires_soon'),
                  link: null,
                },
              ],
            },
          };
        }),
      },
    ];

    const hooks = {
      onInit() {
        if (assets.length > 0) {
          assetStore.loadAssets(assets.map(item => item.id));
        }
      },
      onSuccess(data: Object) {
        const { assigneeId } = data.values().reassignAssetTo;

        assets.forEach(asset => {
          actionDrawerStore.reassignAsset(asset.id, {
            assignee_id: assigneeId,
          });
        });
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        name: 'reassignAsset',
      }
    );
  };

  render() {
    const { t, actionDrawerStore }: TypeProps = this.props;
    const assets = this.props.formData;
    const title = t('general:actions.reassign_asset');

    if (actionDrawerStore.loadingAssets) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <ReassignAssetError />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <ReassignAssetSuccess
            autoClose={this.props.autoClose}
            response={actionDrawerStore.response}
          />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <ReassignAssetForm assets={assets} form={this.createForm()} />
      </div>
    );
  }
}
